import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { Button, Dialog, Divider, DialogTitle, DialogActions, DialogContent } from '@mui/material';

import { formatWebsiteURL } from 'src/utils/masks';

import {
  HostgatorStoreIntegrationType,
  useSaveHostgatorConfigMutation,
  SaveHostgatorConfigMutationVariables,
} from 'src/graphql/generated';

import { RHFTextField } from 'src/components/hook-form';

import {
  HostgatorFormSchema,
  hostgatorConfigFormSchemaDefaultValues,
} from '../schema/hostgator-form-schema';

type TAttributes = 'storeUrl' | 'appKey' | 'storeIntegrationId';

type TCurrentData = Pick<HostgatorStoreIntegrationType, TAttributes>;

type Props = {
  open: boolean;
  onClose: () => void;
  disableForm?: boolean;
  currentData?: TCurrentData | null;
  refetch: () => Promise<ApolloQueryResult<any>>;
};

export function ConfigHostgatorModal({ open, onClose, disableForm, currentData, refetch }: Props) {
  const [enableForm, setEnableForm] = useState<boolean>(!disableForm);
  const [saveHostgatorConfig, { loading }] = useSaveHostgatorConfigMutation();

  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(HostgatorFormSchema),
    defaultValues: hostgatorConfigFormSchemaDefaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const handleClose = () => {
    onClose();
    reset();
    setEnableForm(!disableForm);
  };

  const onSubmit = handleSubmit(async (data) => {
    const { storeUrl, appKey, storeIntegrationId } = data;

    const variables: SaveHostgatorConfigMutationVariables = {
      input: {
        storeUrl,
        appKey,
        storeIntegrationId,
      },
    };

    try {
      const { data: response } = await saveHostgatorConfig({ variables });

      if (response?.saveHostgatorStoreIntegration?.ok === true) {
        enqueueSnackbar('Cadastro efetuado com sucesso!', { variant: 'success' });
      } else {
        enqueueSnackbar('Erro ao salvar os dados da integração', { variant: 'error' });
      }

      setEnableForm(false);
      onClose();
      await refetch();
    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(error?.message, { variant: 'error' });

        return;
      }
      enqueueSnackbar('Não foi possível atualizar o dados!', { variant: 'error' });
    }
  });

  useEffect(() => {
    const updateFields = () => {
      if (!currentData) return;

      const { storeUrl, appKey, storeIntegrationId } = currentData;

      reset({
        storeUrl,
        appKey,
        storeIntegrationId,
      });
    };

    updateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  useEffect(() => {
    setEnableForm(!disableForm);
  }, [disableForm]);

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', md: 600 },
          },
        }}
      >
        <DialogContent
          sx={{
            minWidth: { xs: 300, md: 600 },
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            marginBottom: 2,
            py: 2,
          }}
        >
          <DialogTitle textAlign="center" fontSize={20}>
            Integração HostGator
          </DialogTitle>

          <RHFTextField
            name="storeUrl"
            label="URL da loja"
            disabled={!enableForm}
            helperText="*Copie e cole a url da sua loja HostGator"
            FormHelperTextProps={{
              sx: {
                fontWeight: 600,
              },
            }}
            InputProps={{
              onBlur: (event) => {
                const formatted = formatWebsiteURL(event.target.value);
                setValue('storeUrl', formatted);
              },
            }}
          />

          <RHFTextField name="appKey" label="Chave da Aplicação (APP KEY)" disabled={!enableForm} />

          <RHFTextField
            name="storeIntegrationId"
            label="ID da Loja na HostGator"
            disabled={!enableForm}
          />
        </DialogContent>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Fechar
          </Button>
          {enableForm && (
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={isSubmitting || loading}
              onClick={onSubmit}
            >
              Salvar
            </LoadingButton>
          )}
          {!enableForm && (
            <Button variant="contained" color="primary" onClick={() => setEnableForm(true)}>
              {currentData ? 'Editar' : 'Criar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
